import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';

export default function BacklinkOverviewPage() {
  const [params] = useSearchParams();
  const [domain, setDomain] = useState(params.get('domain') || '');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('backlinks');
  const { updateCredits } = useAuth();

  async function analyze() {
    if (!domain.trim() || loading) return;
    setError(''); setLoading(true); setData(null);
    try {
      const res = await api.post('/backlinks/overview', { domain });
      setData(res.data.data);
      updateCredits(res.data.credits_remaining);
    } catch (e) { setError(e.response?.data?.error || 'Failed'); } finally { setLoading(false); }
  }

  useEffect(() => { if (params.get('domain')) analyze(); }, []);

  return (
    <div className="fade-up">
      <div className="page-header">
        <h1 className="page-title">Backlink Analytics</h1>
        <p className="page-sub">Backlinks, referring domains, anchors, TLD distribution — like SEMrush Backlink Analytics · 4 credits</p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input className="input input-lg" placeholder="Enter domain e.g. semrush.com, ahrefs.com" value={domain} onChange={e => setDomain(e.target.value)} onKeyDown={e => e.key === 'Enter' && analyze()} />
          <button className="btn btn-orange btn-lg" onClick={analyze} disabled={loading || !domain.trim()}>{loading ? 'Analyzing...' : 'Analyze →'}</button>
        </div>
        {error && <div style={{ marginTop: 10, color: '#FF3D57', fontSize: 13, background: '#FFEBEE', padding: '8px 12px', borderRadius: 6 }}>{error}</div>}
      </div>

      {loading && <div className="card"><div className="skeleton" style={{ height: 200 }} /></div>}

      {data && (
        <>
          <div className="metric-grid">
            <div className="card" style={{ margin: 0, textAlign: 'center' }}>
              <div className="metric-label">Authority Score</div>
              <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
                <div className={`authority-score ${data.authorityScore > 70 ? 'as-high' : data.authorityScore > 40 ? 'as-medium' : 'as-low'}`} style={{ width: 64, height: 64, fontSize: 20 }}>{data.authorityScore}</div>
              </div>
              <div className="metric-sub">toxic {data.toxicScore}%</div>
            </div>
            <div className="card" style={{ margin: 0 }}><div className="metric-label">Backlinks</div><div className="metric-value">{data.totalBacklinksFormatted}</div><div className="metric-sub">+{data.newBacklinks} new · -{data.lostBacklinks} lost</div></div>
            <div className="card" style={{ margin: 0 }}><div className="metric-label">Referring Domains</div><div className="metric-value">{data.refDomainsFormatted}</div><div className="metric-sub">{data.refIPs.toLocaleString()} ref IPs</div></div>
            <div className="card" style={{ margin: 0 }}><div className="metric-label">Follow vs Nofollow</div><div style={{ display: 'flex', gap: 4, marginTop: 8 }}><div style={{ flex: data.followVsNofollow.follow, background: '#111', height: 8, borderRadius: 4 }}></div><div style={{ flex: data.followVsNofollow.nofollow, background: '#E5E7EB', height: 8, borderRadius: 4 }}></div></div><div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 6, color: '#6B7280' }}><span>Follow {data.followVsNofollow.follow}%</span><span>Nofollow {data.followVsNofollow.nofollow}%</span></div></div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><span className="card-title">Backlink History (12 months)</span></div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 100 }}>
              {data.history.map((h,i) => {
                const max = Math.max(...data.history.map(x => x.backlinks));
                return <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}><div style={{ width: '100%', background: '#111', height: `${(h.backlinks/max)*100}%`, borderRadius: '3px 3px 0 0' }}></div><span style={{ fontSize: 9, color: '#6B7280' }}>{h.month.slice(0,3)}</span></div>
              })}
            </div>
          </div>

          <div className="card">
            <div className="tabs">
              <button className={`tab-btn ${tab === 'backlinks' ? 'active' : ''}`} onClick={() => setTab('backlinks')}>Backlinks ({data.backlinks.length})</button>
              <button className={`tab-btn ${tab === 'anchors' ? 'active' : ''}`} onClick={() => setTab('anchors')}>Anchors</button>
              <button className={`tab-btn ${tab === 'tld' ? 'active' : ''}`} onClick={() => setTab('tld')}>TLD Distribution</button>
            </div>

            {tab === 'backlinks' && (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Source</th><th>Target</th><th>Anchor</th><th>AS</th><th>Type</th><th>Follow</th></tr></thead>
                  <tbody>
                    {data.backlinks.map((b,i) => (
                      <tr key={i}>
                        <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>{b.sourceDomain}</td>
                        <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, color: '#6B7280' }}>{b.targetUrl}</td>
                        <td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.anchor}</td>
                        <td><span className={`badge ${b.authorityScore > 70 ? 'badge-green' : b.authorityScore > 40 ? 'badge-yellow' : 'badge-red'}`}>{b.authorityScore}</span></td>
                        <td><span className="badge badge-gray" style={{ fontSize: 10 }}>{b.type}</span></td>
                        <td><span className={`badge ${b.follow === 'Follow' ? 'badge-green' : 'badge-gray'}`} style={{ fontSize: 10 }}>{b.follow}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'anchors' && (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Anchor Text</th><th>Count</th><th>Percent</th></tr></thead>
                  <tbody>
                    {data.anchors.map((a,i) => (
                      <tr key={i}><td style={{ fontWeight: 600 }}>{a.anchor}</td><td>{a.count.toLocaleString()}</td><td>{a.percent}%</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'tld' && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {data.tldDistribution.map(t => (
                  <div key={t.tld} className="card" style={{ margin: 0, flex: '1 1 120px', textAlign: 'center' }}>
                    <div style={{ fontWeight: 800, fontSize: 20 }}>{t.tld}</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>{t.count.toLocaleString()} domains</div>
                    <div style={{ fontSize: 11, background: '#F3F4F6', borderRadius: 99, padding: '2px 8px', display: 'inline-block', marginTop: 6 }}>{t.percent}%</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
